// =============================================================================
// useWorkerJob — realtime status aktywnego worker job dla (mapId, step)
// =============================================================================
// Pokazuje aktywny queue row (status=queued/in_progress) plus ostatni progress
// label z config.progress_log. Uzywany w SeedsTab/NodesTab do wyswietlenia
// banneru "⚙ Worker generuje seedy... KG: 10 entities" gdy worker dziala.
//
// Supabase Realtime subscription na generation_queue — kazdy UPDATE (np. push
// do progress_log przez worker) triggeruje refetch. Debounce 500ms zeby nie
// spamowac queries.

import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRealtimeSubscription } from "./useRealtimeSubscription";

export interface WorkerJobState {
  queueRowId: string;
  status: "queued" | "in_progress" | "completed" | "failed" | "cancelled";
  startedAt: string | null;
  progressLabel: string | null;
  progressLog: Array<{ t: string; stage: string; label: string }>;
  error: string | null;
  seedId: string | null;
}

/**
 * @param mapId — mapa do ktorej szukamy aktywnego joba
 * @param step — 'map_seeds' | 'map_nodes' | inny prefix
 * @param seedId — opcjonalny filtr (dla nodes per seed)
 */
export function useWorkerJob(
  mapId: string | undefined | null,
  step: string,
  seedId?: string,
): {
  job: WorkerJobState | null;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const [job, setJob] = useState<WorkerJobState | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchJob = useCallback(async () => {
    if (!mapId) {
      setJob(null);
      return;
    }
    setLoading(true);

    // Szukamy ostatniego aktywnego lub recent (ostatnie 5 min) job dla (mapId, step, seedId?)
    const since = new Date(Date.now() - 5 * 60_000).toISOString();
    const { data } = await (supabase as any)
      .from("generation_queue")
      .select("id, status, started_at, completed_at, error, config")
      .eq("map_id", mapId)
      .eq("step", step)
      .or(`status.eq.queued,status.eq.in_progress,and(status.eq.completed,completed_at.gte.${since}),and(status.eq.failed,completed_at.gte.${since})`)
      .order("created_at", { ascending: false })
      .limit(5);

    const rows = (data || []) as Array<{
      id: string;
      status: WorkerJobState["status"];
      started_at: string | null;
      completed_at: string | null;
      error: string | null;
      config: Record<string, unknown> | null;
    }>;

    // Filter po seedId jesli podany
    const filtered = seedId
      ? rows.filter((r) => (r.config as { seedId?: string })?.seedId === seedId)
      : rows;

    if (filtered.length === 0) {
      setJob(null);
      setLoading(false);
      return;
    }

    const row = filtered[0];
    const progressLog = (row.config as { progress_log?: Array<{ t: string; stage: string; label: string }> })?.progress_log ?? [];
    const lastEntry = progressLog[progressLog.length - 1];

    setJob({
      queueRowId: row.id,
      status: row.status,
      startedAt: row.started_at,
      progressLabel: lastEntry?.label ?? null,
      progressLog,
      error: row.error,
      seedId: (row.config as { seedId?: string })?.seedId ?? null,
    });
    setLoading(false);
  }, [mapId, step, seedId]);

  useEffect(() => {
    fetchJob();
  }, [fetchJob]);

  // Realtime subscription na generation_queue — filter dla mapId
  useRealtimeSubscription({
    channel: `worker-job-${mapId}-${step}${seedId ? `-${seedId}` : ""}`,
    table: "generation_queue",
    onChange: fetchJob,
    debounceMs: 500,
  });

  return { job, loading, refresh: fetchJob };
}
